<template>
  <li class="detail" :class="{ 'is-open': isOpenList }" @click="isOpenList = !isOpenList">
    <div class="bar">
      <ReceptionStatus :status="detail.status" />
      <span class="created-at">
        受付：{{ formatDate(detail.receiptDatetime, 'YYYY/M/D (dd) HH:mm') }} {{ detail.inputBy }}
      </span>
      <ArrowIcon class="arrow" />
    </div>
    <ul v-show="isOpenList" class="reserve-info">
      <li class="item reserve-name">
        <p
          class="customer-name"
          :class="[{ 'is-modify': displayName !== beforeDisplayName }, { suffix: !isBlock(detail.type) }]"
        >
          {{ displayName }}
        </p>
        <p v-if="kanaName" class="customer-kana" :class="{ 'is-modify': kanaName !== beforeKanaName }">
          {{ kanaName }}
        </p>
      </li>
      <li v-if="customer.tel1 || customer.tel2" class="item reserve-tel">
        <TelIcon />
        <div class="text">
          <span v-if="customer.tel1" :class="{ 'is-modify': !compareTo('customer.tel1') }">{{ customer.tel1 }}</span>
          <span v-if="customer.tel1 && customer.tel2">/</span>
          <span v-if="customer.tel2" :class="{ 'is-modify': !compareTo('customer.tel2') }">{{ customer.tel2 }}</span>
        </div>
      </li>
      <li class="item reserve-date">
        <CalendarIcon />
        <span :class="{ 'is-modify': !compareTo('reserveDatetime.date') }">
          {{ formatDate(detail.reserveDatetime.date, 'YYYY / M / D (dd)') }}
        </span>
      </li>
      <li class="item reserve-time">
        <ClockIcon />
        <span
          :class="{
            'is-modify': !compareTo('reserveDatetime.time') || !compareTo('utilizationTime') || !compareTo('resetTime'),
          }"
        >
          {{ formatTime(detail.reserveDatetime.time) }} 〜 {{ endTime }}
          {{ isBlock(detail.type) ? '' : `（リセット${detail.resetTime}分）` }}
        </span>
      </li>
      <li v-if="reserveNumDisplay(detail)" class="item reserve-people">
        <AddPeopleIcon />
        <span :class="{ 'is-modify': !compareTo('adults') || !compareTo('children') }">
          {{ reserveNumDisplay(detail) }}
        </span>
      </li>
      <li class="item reserve-table">
        <TableIcon />
        <div class="text" :class="{ 'is-modify': !compareTo('tables') }">
          <span v-if="!detail.tables">未指定</span>
          <span v-else v-for="(table, index) in detail.tables" :key="`index${index}`">{{ table.name }}</span>
        </div>
      </li>
      <li v-if="detail.confirmFlag" class="item no-change" :class="{ 'is-modify': !compareTo('confirmFlag') }">
        予約情報変更なし
      </li>
      <li v-if="isSP && alert" class="item reserve-alert">{{ alert }}</li>
    </ul>
    <ul v-show="isOpenList" class="reserve-customer-info">
      <li v-if="detail.tags && detail.tags.length" class="item tags"><TagLabels :tags="detail.tags" /></li>
      <template v-if="detail.courses">
        <li class="item reserve-course" v-for="(course, index) in detail.courses" :key="`index${index}`">
          <KnifeForkIcon />
          <span :class="{ 'is-modify': !compareTo('courses') }">
            {{ course.name }}{{ isGnavi(detail.syncType) ? ` / ¥${course.price.toLocaleString()}` : '' }} ×
            {{ course.count }}名様
          </span>
        </li>
      </template>
      <li v-if="detail.type" class="item reserve-method">
        <AssignIcon />
        <span :class="{ 'is-modify': !compareTo('type') }">{{ typeMap[detail.type] }}</span>
      </li>
      <li v-if="detail.purpose" class="item reserve-purpose">
        <TagIcon />
        <span :class="{ 'is-modify': !compareTo('purpose') }">{{ purposeMap[detail.purpose] }}</span>
      </li>
      <li v-if="detail.media" class="item reserve-media">
        <span class="head">媒体 :</span>
        <span :class="{ 'is-modify': !compareTo('media') }">{{ medias.byId[detail.media] }}</span>
      </li>
      <li v-if="detail.settlement" class="item reserve-payment">
        <span class="head">決済情報 :</span>
        <span :class="{ 'is-modify': !compareTo('settlement') || !compareTo('settlementPoint') }">
          {{ settlementMap[detail.settlement] }}
          {{ pointPayment(detail.settlement) ? `${couponPoint(detail)}${couponPointNote(detail)}` : '' }}
        </span>
      </li>
      <li v-if="detail.coupon" class="item reserve-coupon">
        <div>
          <span class="head">ぐるなびネット予約クーポン :</span>
          <span :class="{ 'is-modify': !compareTo('coupon.title') }">{{ detail.coupon.title }}</span>
        </div>
        <div v-if="detail.coupon.text" class="text">{{ detail.coupon.text }}</div>
      </li>
      <li v-if="detail.reserveName" class="item reserve-customer-name">
        <span class="head">ご予約名 :</span>
        <span :class="{ 'is-modify': !compareTo('reserveName') }">{{ detail.reserveName }} 様</span>
      </li>
      <li v-if="detail.remarks" class="item reserve-remarks">
        <span class="head">備考 :</span>
        <span :class="{ 'is-modify': !compareTo('remarks') && !detail.autoImportFlag }">{{ detail.remarks }}</span>
      </li>
      <li class="item reserve-visit-status">
        <span class="head">ステータス :</span>
        <span :class="{ 'is-modify': !compareTo('visitStatus') }">{{ statusMap(detail.visitStatus) }}</span>
      </li>
    </ul>
  </li>
</template>

<script>
import { get, isEqual } from 'lodash'
import { mapState } from 'vuex'
import { formatDate, getEndTime } from '@/utils/date'
import { formatTime } from '@/utils/time'
import { reserveName, reserveNumDisplay, couponPoint, couponPointNote } from '@/utils/reserve'
import purposeMap from '@/resources/reservation/purposeMap'
import { isGnavi } from '@/resources/reservation/syncTypeMap'
import typeMap, { isBlock } from '@/resources/reservation/typeMap'
import settlementMap, { pointPayment } from '@/resources/reservation/settlementMap'
import statusMap from '@/resources/reservation/statusMap'
import TagLabels from '@/components/molecules/TagLabels'
import ReceptionStatus from '@/components/atoms/ReceptionStatus'
import ArrowIcon from '@/components/icons/arrowLight'
import TelIcon from '@/components/icons/TelIcon'
import CalendarIcon from '@/components/icons/CalendarIcon'
import ClockIcon from '@/components/icons/clock'
import AddPeopleIcon from '@/components/icons/addPeople'
import TableIcon from '@/components/icons/table'
import KnifeForkIcon from '@/components/icons/knifeFork'
import AssignIcon from '@/components/icons/assign'
import TagIcon from '@/components/icons/tag'

export default {
  name: 'DetailList',
  components: {
    TagLabels,
    ReceptionStatus,
    ArrowIcon,
    TelIcon,
    CalendarIcon,
    ClockIcon,
    AddPeopleIcon,
    TableIcon,
    KnifeForkIcon,
    AssignIcon,
    TagIcon,
  },
  props: {
    detail: { type: Object },
    compare: { type: Object },
    isOpen: { type: Boolean },
    isSP: { type: Boolean },
    hasDuplicatedIds: { type: Boolean },
    tebledUnsettled: { type: Boolean },
  },
  data() {
    return {
      isOpenList: this.isOpen,
      purposeMap,
      typeMap,
      settlementMap,
    }
  },
  computed: {
    ...mapState({
      medias: state => state.medias,
    }),
    customer() {
      return this.detail.customer
    },
    displayName() {
      return reserveName({ ...this.detail, reserveName: null })
    },
    beforeDisplayName() {
      return reserveName({ ...this.compare, reserveName: null })
    },
    kanaName() {
      const { firstName, lastName, firstNameKana, lastNameKana } = this.detail.customer
      return (lastName || firstName) && (lastNameKana || firstNameKana)
        ? `${lastNameKana || ''} ${firstNameKana || ''}`
        : ''
    },
    beforeKanaName() {
      const { firstName, lastName, firstNameKana, lastNameKana } = this.compare.customer
      return (lastName || firstName) && (lastNameKana || firstNameKana)
        ? `${lastNameKana || ''} ${firstNameKana || ''}`
        : ''
    },
    endTime() {
      const { hour, minute } = this.detail.reserveDatetime
      return getEndTime(hour, minute, this.detail.utilizationTime)
    },
    alert() {
      if (!this.isOpen) return false
      let text
      if (this.hasDuplicatedIds) {
        text = '予約が重複しています'
      } else if (this.tebledUnsettled) {
        text = 'テーブルを選択してください'
      }
      return text
    },
  },
  methods: {
    isGnavi,
    formatDate,
    formatTime,
    reserveNumDisplay,
    couponPoint,
    couponPointNote,
    pointPayment,
    isBlock,
    compareTo(target) {
      return isEqual(get(this.compare, target), get(this.detail, target))
    },
    statusMap(status) {
      return statusMap[status]
    },
  },
}
</script>

<style lang="postcss" scoped>
@custom-media --small-width (width < 480px);

.detail {
  background-color: var(--white);
  cursor: pointer;
  padding: 24px;
  position: relative;

  & + & {
    margin-top: 16px;
  }
}

.bar {
  align-items: center;
  display: grid;
  grid-gap: 8px;
  grid-template-columns: auto 1fr auto;
  margin-right: -4px;
}

.created-at {
  font-size: 13px;
  line-height: 1;
  text-align: right;
  word-break: keep-all;
}

.arrow {
  fill: var(--brand-color);
  height: 40px;
  margin: -8px;
  width: 40px;

  .is-open & {
    transform: rotate(180deg);
  }
}

.reserve-info {
  border-bottom: 1px solid var(--border-color);
  padding-bottom: 16px;
}

.reserve-info,
.reserve-customer-info {
  display: grid;
  grid-gap: 8px;
  margin-top: 16px;
}

.item {
  display: grid;
  grid-gap: 8px;
  grid-template-columns: auto 1fr;

  & svg {
    fill: var(--text-color);
  }

  &.reserve-coupon {
    grid-template-columns: none;
    line-height: 26px;
  }

  .head {
    font-weight: bold;
  }
}

.text {
  span {
    margin-right: 12px;
  }
}

.reserve-coupon {
  .text {
    font-size: 13px;
    line-height: 20px;
    white-space: pre-wrap;
  }
}

.reserve-name {
  grid-auto-flow: row;
  grid-gap: 0;
  grid-template-columns: auto;
}

.customer-name {
  font-size: 21px;
  font-weight: 600;
  position: relative;
  &.suffix::after {
    color: var(--text-color);
    content: '様';
    font-size: 15px;
    font-weight: normal;
    margin-left: 8px;
  }
}

.customer-kana {
  font-size: 11px;
}

.no-change {
  padding-left: 32px;
}

.reserve-alert {
  background: var(--pure-red);
  border-radius: 12px;
  color: var(--white);
  display: block;
  padding: 8px 0;
  text-align: center;
  width: 100%;
}

.reserve-remarks {
  white-space: pre-line;
}

.is-modify {
  color: var(--pure-red);
}

.reserve-payment {
  display: block;

  span:first-of-type {
    margin-right: 8px;
  }
}

.responsive {
  @media screen and (--small-width) {
    .detail {
      padding: 16px;
    }

    .created-at {
      font-size: 11px;
    }

    .arrow {
      height: 24px;
      margin: 0;
      width: 24px;
    }

    .item {
      line-height: 1;
      & svg {
        height: 16px;
        width: 16px;
      }
    }

    .customer-name {
      font-size: 20px;
    }

    .customer-kana {
      margin-top: 8px;
    }

    .no-change {
      padding-left: 24px;
    }

    .reserve-tel {
      margin-top: 8px;
    }

    .reserve-coupon {
      line-height: 21px;
    }

    .reserve-remarks {
      display: block;
      line-height: 1.4;
    }

    .reserve-payment {
      line-height: 1.4;
    }
  }
}
</style>
